import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  cartOpen: false,
  cartItems: [],
  submittedPromoCodes: []
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    toggleCart: (state) => {
      state.cartOpen = !state.cartOpen;
    },
    openCart: (state) => {
      state.cartOpen = true;
    },
    closeCart: (state) => {
      state.cartOpen = false;
    },

    addToCart: (state, action) => {
      const { id, title, sku, flavor, price, subscription, imageId, quantity = 1, uploadedImageIds = [], image_metadata, bundleId, bundleName, bundleDiscount, bundleTotal, bundleRawTotal } = action.payload;

      // Explicit personalization wins (cart restore and the SDK designer flow pass it
      // under this dedicated key — a top-level product spread can't trigger it). All
      // other flows keep reading the customizer's localStorage keys as before.
      const explicit = action.payload.personalization || null;
      // When a personalization object is provided, its values are authoritative even
      // when null (a restored item with no pet name must NOT inherit this device's
      // stale localStorage pet name). Without it, legacy localStorage reads apply.
      const petName = explicit ? (explicit.petName ?? null) : localStorage.getItem("petName");
      const petType = explicit ? (explicit.petType ?? null) : localStorage.getItem("petType");
      const imageMetadata = image_metadata || localStorage.getItem("imageMetadata");
      const labelTemplate = explicit ? (explicit.labelTemplate ?? null) : localStorage.getItem("label_template");
      const cartId = `${id}-${subscription ? "sub" : "reg"}-${imageId}${bundleId ? `-bundle-${bundleId}` : ''}`;
      const bundleCartId = bundleId ? `bundle-${bundleId}-${imageId}` : null
      const existingItem = state.cartItems.find((item) => item.cartId === cartId);

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        state.cartItems.push({
          id,
          sku,
          cartId,
          name: title,
          petType,
          petName,
          price,
          tag: flavor,
          quantity,
          active: true,
          subscription,
          imageId,
          uploadedImageIds,
          labelTemplate,
          imageMetadata,
          bundleId,
          bundleCartId,
          bundleName,
          bundleDiscount,
          bundleTotal,
          bundleRawTotal,
          // SDK designer provenance (asset ids, variant, draft) — null for legacy items.
          tk: action.payload.tk ?? null,
        });
      }
    },

    removeFromCart: (state, action) => {
      state.cartItems = state.cartItems.filter((item) => item.cartId !== action.payload);
    },

    toggleItemActive: (state, action) => {
      const item = state.cartItems.find((item) => item.cartId === action.payload);
      if (item) {
        item.active = !item.active;
      }
    },

    updateQuantity: (state, action) => {
      const { cartId, change } = action.payload;
      const itemIndex = state.cartItems.findIndex((item) => item.cartId === cartId);

      if (itemIndex !== -1) {
        state.cartItems[itemIndex].quantity = Math.max(1, state.cartItems[itemIndex].quantity + change);
      }
    },

    clearCart: (state) => {
      state.cartItems = [];
    },

    toggleSubscription: (state, action) => {
      const cartId = action.payload;
      const itemToToggle = state.cartItems.find((item) => item.cartId === cartId);
      if (!itemToToggle) return;

      const match = itemToToggle.cartId.match(/^(.+)-(sub|reg)-(.+)$/);

      if (!match) return;

      const [, baseId, currentType, suffix] = match;
      const newType = currentType === "sub" ? "reg" : "sub";
      const newCartId = `${baseId}-${newType}-${suffix}`;
      const existingItem = state.cartItems.find((item) => item.cartId === newCartId);

      if (existingItem) {
        existingItem.quantity += itemToToggle.quantity;
        state.cartItems = state.cartItems.filter((item) => item.cartId !== cartId);
      } else {
        Object.assign(itemToToggle, {
          cartId: newCartId,
          subscription: !itemToToggle.subscription
        });
      }
    },

    applyPromoCode: (state, action) => {
      const { code, discount_amount, discount_type, free_shipping, max_order_total, is_affiliate } = action.payload;

      const exists = state.submittedPromoCodes.find(
        (p) => p.code.toLowerCase() === code.toLowerCase()
      );
      if (!exists) {
        state.submittedPromoCodes.push({ code, discount_amount, discount_type, free_shipping, max_order_total, is_affiliate });
      }
    },
    removePromoCode: (state, action) => {
      state.submittedPromoCodes = state.submittedPromoCodes.filter(
        (p) => p.code.toLowerCase() !== action.payload.toLowerCase()
      );
    },
    removePromoCodes: (state, action) => {
      const codesToRemove = action.payload.map(code => code.toLowerCase());
      state.submittedPromoCodes = state.submittedPromoCodes.filter(
        (p) => !codesToRemove.includes(p.code.toLowerCase())
      );
    },
    clearPromoCodes: (state) => {
      state.submittedPromoCodes = [];
    },
  },
});

export const {
  toggleCart,
  openCart,
  closeCart,
  addToCart,
  removeFromCart,
  toggleItemActive,
  updateQuantity,
  clearCart,
  toggleSubscription,
  applyPromoCode,
  removePromoCode,
  removePromoCodes,
  clearPromoCodes,
} = cartSlice.actions;

export default cartSlice.reducer;
export { initialState as cartInitialState };
